import { pageTitle } from '@palaccount/config';
import { STOCK_MESSAGES, type StockMovementRow } from '@palaccount/contracts';
import { Loader2, PencilLine, RotateCcw, ScanLine, Search, SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Typeahead } from '@/components/ui/typeahead';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { formatQty } from '@/lib/format';
import { usePagedState, usePageRange } from '@/lib/paging';
import { denyPermission } from '@/lib/permissions';
import { ApiError, localizeServerMessage } from '@/lib/server-messages';
import { useBaseCurrency } from '@/lib/session';
import { cn } from '@/lib/utils';

import {
  blockKey,
  captureBlocks,
  stillMissing,
  trackingPayload,
  type CaptureText,
  type TrackedItem,
} from '@/features/sales/tracking-capture';

import { useItemCategories } from './api';
import { useItemPicker } from './item-picker';
import { InventoryTabs } from './inventory-tabs';
import { Modal } from './modal';
import { Chip, MovementKindPill } from './stock-chips';
import {
  useCorrectMovement,
  useManualAdjust,
  useStockMovements,
  useWarehouses,
} from './stock-api';
import {
  MOVEMENT_KINDS,
  movementDotClass,
  signedQtyClass,
  signedQtyWithUnit,
} from './stock-format';

const QTY_RE = /^\d{1,12}(\.\d{1,3})?$/;

const GROUPS = ['sales', 'purchases', 'transfers', 'adjustments', 'returns', 'bundles'] as const;

/**
 * BR-INV-06-06/-07/-08 — the ONE movement a human writes by hand: a positive
 * magnitude, a direction that supplies the sign, and a reason that is not
 * optional. Save stays disabled until both are real, and the fixed sentence
 * sits under the box while it is blank.
 */
function ManualAdjustmentModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();

  const { data: warehouses } = useWarehouses({ status: 'active' });
  const adjust = useManualAdjust();

  const [itemId, setItemId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [qty, setQty] = useState('');
  const [direction, setDirection] = useState<'increase' | 'decrease'>('increase');
  const [reason, setReason] = useState('');
  const [tracking, setTracking] = useState<CaptureText>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * GEN-50 — searched, not listed. A Service never appears in a stock
   * movement (BR-MD-03-07), and that narrowing is the server's `stockable`
   * predicate: applying it to a page in the browser would hide matches the
   * search had already found.
   */
  const itemPicker = useItemPicker([itemId], { status: 'active', stockable: true });
  const item = itemPicker.byId.get(itemId);

  // BR-MD-03-17 here too: a hand-written movement of a tracked item names its
  // units. `expandKits: false` because this endpoint moves the item itself.
  const blocks = captureBlocks(item as TrackedItem | undefined, qty, { expandKits: false });

  /*
   * ERR-INV-06-02 — "clicked Save with quantity zero | the button stays grey |
   * type a quantity". Unusually for a §14 row this one describes CORRECT
   * behaviour rather than a defect, and INV-06 §15 says the same thing from the
   * other side: "a zero-quantity movement must never be written. The adjustment
   * modal already refuses it."
   *
   * The second test is the one that does the work and it is not redundant with
   * the first: `QTY_RE` accepts `0`, `0.0` and `000` as well-formed numbers, so
   * without the zero test a grey button would turn blue on a movement that
   * moves nothing. Refusing at the door — never a silent skip — is the same
   * shape INV-08's voucher takes for a zero line.
   */
  const qtyOk = QTY_RE.test(qty.trim()) && !/^0+(\.0+)?$/.test(qty.trim());
  const reasonOk = reason.trim() !== '';
  const ready = Boolean(itemId) && Boolean(warehouseId) && qtyOk && reasonOk;

  const onSave = async () => {
    setBusy(true);
    setError(null);
    const identified = trackingPayload(blocks, tracking);
    try {
      await adjust.mutateAsync({
        itemId,
        warehouseId,
        qty: qty.trim(),
        direction,
        reason: reason.trim(),
        ...(identified.serialNos ? { serialNos: identified.serialNos } : {}),
        ...(identified.batchNo ? { batchNo: identified.batchNo } : {}),
      });
      onClose();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('inventory.edit');
      // D46: the cause rides along — a structured stock refusal (itemInactive,
      // the serial family, insufficientStock…) or the warehouse wall
      // (wh.errClosed at the stock writer itself) localizes by key+params;
      // anything else keeps the server's own sentence.
      setError(
        localizeServerMessage(t, thrown instanceof Error ? thrown.message : String(thrown), thrown),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t('mv.manualTitle')} onClose={onClose} className="max-w-lg">
      <div className="grid gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="ma-item">{t('mv.fItem')}</Label>
          <Typeahead
            id="ma-item"
            label={t('mv.fItem')}
            value={itemId || null}
            onChange={(id) => setItemId(id ?? '')}
            options={itemPicker.options}
            selected={itemPicker.optionFor(itemId)}
            query={itemPicker.query}
            onQueryChange={itemPicker.setQuery}
            placeholder={t('mv.pickItem')}
            emptyLabel={t('picker.noMatchingItem')}
            loading={itemPicker.loading}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="ma-warehouse">{t('mv.fWarehouse')}</Label>
          <NativeSelect
            id="ma-warehouse"
            value={warehouseId}
            onChange={(event) => setWarehouseId(event.target.value)}
          >
            <option value="">{t('mv.pickWarehouse')}</option>
            {(warehouses ?? []).map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="ma-qty">
            {item ? t('mv.fQtyWithUnit', { unit: item.stockUnitSymbol }) : t('mv.fQty')}
          </Label>
          <Input
            id="ma-qty"
            dir="ltr"
            inputMode="decimal"
            className="font-mono"
            value={qty}
            onChange={(event) => setQty(event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <span className="text-sm font-medium">{t('mv.fDirection')}</span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={direction === 'increase' ? 'default' : 'outline'}
              className={cn(direction === 'increase' && 'bg-emerald-600 hover:bg-emerald-700')}
              onClick={() => setDirection('increase')}
            >
              {t('mv.increase')}
            </Button>
            <Button
              type="button"
              variant={direction === 'decrease' ? 'default' : 'outline'}
              className={cn(direction === 'decrease' && 'bg-red-600 hover:bg-red-700')}
              onClick={() => setDirection('decrease')}
            >
              {t('mv.decrease')}
            </Button>
          </div>
        </div>
        {/*
          OQ-05-17's block, in the shape a dialog can hold: one per thing that
          moves, saying what is still outstanding. The count is the point —
          the same sentence a grid's sub-table shows, so a clerk meets one
          rule with one habit wherever stock is moved by hand.
        */}
        {blocks.map((block) => {
          const key = blockKey(block);
          const typed = tracking[key] ?? '';
          const missing = stillMissing(block, typed);
          return (
            <div key={key} className="grid gap-1.5">
              <Label htmlFor="ma-tracking" className="flex items-center gap-2">
                <ScanLine className="size-4 text-muted-foreground" aria-hidden />
                {t('tracking.forItem', { name: block.name })}
              </Label>
              <Input
                id="ma-tracking"
                aria-label={
                  block.kind === 'serial'
                    ? t('mv.serialsFor', { name: block.name })
                    : t('mv.batchFor', { name: block.name })
                }
                dir="ltr"
                className={cn(
                  'font-mono',
                  missing > 0 && 'border-amber-400 dark:border-amber-600',
                )}
                placeholder={
                  block.kind === 'serial' ? t('tracking.serialsPh') : t('tracking.batchPh')
                }
                value={typed}
                onChange={(event) => setTracking({ ...tracking, [key]: event.target.value })}
              />
              <p
                className={cn(
                  'text-xs font-medium',
                  missing > 0
                    ? 'text-amber-700 dark:text-amber-400'
                    : 'text-emerald-700 dark:text-emerald-400',
                )}
              >
                {missing > 0
                  ? t('tracking.stillToPick', { count: missing })
                  : t('tracking.allPicked')}
              </p>
            </div>
          );
        })}
        <div className="grid gap-1.5">
          <Label htmlFor="ma-reason">{t('mv.fReason')}</Label>
          <Textarea
            id="ma-reason"
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {/* ERR-INV-06-01, the server's own sentence, shown before the trip. */}
          {reasonOk ? null : (
            <p className="text-xs text-muted-foreground">
              {localizeServerMessage(t, STOCK_MESSAGES.reasonRequired)}
            </p>
          )}
        </div>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void onSave()} disabled={!ready || busy}>
            {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** BR-INV-06-02's only correction path: an opposite entry, never an edit. */
function CorrectModal({ row, onClose }: { row: StockMovementRow; onClose: () => void }) {
  const { t } = useTranslation();
  const correct = useCorrectMovement();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await correct.mutateAsync({ movementId: row.id, reason: reason.trim() });
      onClose();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('inventory.approve');
      // D46: the cause rides along — alreadyCorrected names the movement code
      // in its params, and a correction posts through the same stock writer,
      // so wh.errClosed can arrive here too.
      setError(
        localizeServerMessage(t, thrown instanceof Error ? thrown.message : String(thrown), thrown),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t('mv.correctTitle')} onClose={onClose} className="max-w-md">
      <div className="grid gap-4">
        <p className="text-sm text-muted-foreground">
          {t('mv.correctIntro', { code: row.code })}
        </p>
        <div className="grid gap-1.5">
          <Label htmlFor="mc-reason">{t('mv.fReason')}</Label>
          <Textarea
            id="mc-reason"
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {reason.trim() === '' ? (
            <p className="text-xs text-muted-foreground">
              {localizeServerMessage(t, STOCK_MESSAGES.reasonRequired)}
            </p>
          ) : null}
        </div>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void onConfirm()} disabled={reason.trim() === '' || busy}>
            {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
            {t('mv.correct')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * INV-06 — the append-only ledger. Read-only for every role including the
 * Owner (BR-INV-06-02): no edit, no delete, only the manual adjustment and
 * the opposite-entry correction. Manual rows wash amber (BR-INV-06-09) and
 * transfer legs carry the blue rail that pairs them across two locations.
 */
export function StockMovementsPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t } = useTranslation();

  const [q, setQ] = useState('');
  const [reference, setReference] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [kinds, setKinds] = useState<string[]>([]);
  const [warehouseIds, setWarehouseIds] = useState<string[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [moreFilters, setMoreFilters] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [correcting, setCorrecting] = useState<StockMovementRow | null>(null);
  /**
   * NFR-42. The page is in the URL, so "the movement that went wrong is on
   * page 12" is a sentence somebody can send rather than describe, and every
   * one of the seven filters resets to page 1 through `paging.change`.
   */
  const paging = usePagedState();

  useEffect(() => {
    document.title = pageTitle(t('mv.title'));
  }, [t]);

  const { data, isPending, isError } = useStockMovements(
    {
      q: q.trim() || undefined,
      reference: reference.trim() || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      kinds,
      warehouseIds,
      categoryId: categoryId || undefined,
    },
    paging.page,
    paging.pageSize,
  );
  const range = usePageRange(data, paging.pageSize);
  const { data: warehouses } = useWarehouses({ status: 'active' });
  const { data: categories } = useItemCategories(true);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const summary = data?.summary;

  /** Rows a correction already answers — the server 409s either way. */
  const corrected = useMemo(
    () => new Set(rows.map((row) => row.correctsMovementId).filter((id): id is string => id !== null)),
    [rows],
  );

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('mv.title')}</h2>
        <Gated permission="inventory.edit" reasonKey="perm.cannot.adjustStock">
          <Button onClick={() => setManualOpen(true)}>
            <PencilLine aria-hidden /> {t('mv.manualAdjust')}
          </Button>
        </Gated>
      </div>

      {/* INV-06 §5.1 — these cards DO respond to the filters. */}
      {/* TODO(INV-06 §5.1): the movements-over-time trend chart. */}
      <div className="grid gap-3 lg:grid-cols-3">
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('mv.cardTotal')}</span>
            <span className="font-mono text-2xl font-bold">{summary?.totalMovements ?? '—'}</span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid gap-1 py-4">
            <span className="text-xs text-muted-foreground">{t('mv.cardNet')}</span>
            {summary ? (
              <Amount value={summary.netValue} currency={baseCurrency} className="text-2xl font-bold" />
            ) : (
              <span className="font-mono text-2xl font-bold">—</span>
            )}
            {/*
              BR-INV-06-15 / N-14: quantities are never summed across units, so
              the server sends `netQty` only when every filtered movement shares
              ONE unit — and `netQtyUnit` with it, which is what lets this
              figure obey N-4 too. When the filter spans units both are null and
              the money net is the headline, which is N-14's first rule ("what
              adds up across items must add up MONEY").

              This condition is where ERR-INV-06-04 stops being possible. The
              §14 row reports "read the Net stock change number | A NUMBER THAT
              ADDS REAMS TO PIECES | read the money figure underneath instead",
              and the build inverts the row's own layout to answer it: money is
              the headline ABOVE and the quantity is the subordinate line, shown
              only in the one case where adding was legitimate. The row's
              remedy therefore needs no user — there is no cross-unit number on
              the screen to be misread, and when the filter spans units the hint
              below says so in words instead of printing a figure.
            */}
            {summary?.netQty && summary.netQtyUnit ? (
              <span className="text-sm text-muted-foreground">
                <Ltr
                  value={signedQtyWithUnit(
                    summary.netQty,
                    summary.netQtyUnit.precision,
                    summary.netQtyUnit.symbol,
                  )}
                  className="font-mono"
                />
              </span>
            ) : summary ? (
              <span className="text-xs text-muted-foreground">{t('mv.netQtyHint')}</span>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="grid gap-2 py-4">
            <span className="text-xs text-muted-foreground">{t('mv.cardByType')}</span>
            <dl className="grid grid-cols-3 gap-2 text-xs">
              {GROUPS.map((group) => (
                <div key={group} className="grid gap-0.5">
                  <dt className="text-muted-foreground">{t(`mv.group.${group}`)}</dt>
                  <dd className="font-mono text-base font-semibold">
                    {summary?.byGroup[group] ?? '—'}
                  </dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="grid gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="relative w-full max-w-[280px]">
              <Search
                className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                aria-label={t('mv.searchLabel')}
                placeholder={t('mv.searchPh')}
                value={q}
                onChange={(event) => paging.change(setQ)(event.target.value)}
                className="h-10 ps-9 text-sm"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="mv-reference">{t('mv.fReference')}</Label>
              <Input
                id="mv-reference"
                className="h-10 w-40 font-mono text-sm"
                value={reference}
                onChange={(event) => paging.change(setReference)(event.target.value)}
              />
            </div>
            {/* BR-INV-06-16: both bounds inclusive. */}
            <div className="grid gap-1.5">
              <Label htmlFor="mv-from">{t('mv.fDateFrom')}</Label>
              <Input
                id="mv-from"
                type="date"
                dir="ltr"
                className="h-10 w-40 text-sm"
                value={dateFrom}
                onChange={(event) => paging.change(setDateFrom)(event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="mv-to">{t('mv.fDateTo')}</Label>
              <Input
                id="mv-to"
                type="date"
                dir="ltr"
                className="h-10 w-40 text-sm"
                value={dateTo}
                onChange={(event) => paging.change(setDateTo)(event.target.value)}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ms-auto"
              aria-expanded={moreFilters}
              onClick={() => setMoreFilters((current) => !current)}
            >
              <SlidersHorizontal aria-hidden /> {t('mv.moreFilters')}
            </Button>
          </div>

          {/* INV-06 §5.3 — the eleven kinds, each with its own colour. */}
          <div className="flex flex-wrap gap-2">
            {MOVEMENT_KINDS.map((kind) => (
              <Chip
                key={kind}
                active={kinds.includes(kind)}
                dotClass={movementDotClass(kind)}
                onClick={() => paging.change(setKinds)(toggle(kinds, kind))}
              >
                {t(`mv.kind.${kind}`)}
              </Chip>
            ))}
          </div>

          {moreFilters ? (
            <div className="flex flex-wrap items-center gap-3 rounded-md border p-3">
              <span className="text-xs text-muted-foreground">{t('mv.filterWarehouses')}</span>
              {(warehouses ?? []).map((warehouse) => (
                <Chip
                  key={warehouse.id}
                  active={warehouseIds.includes(warehouse.id)}
                  onClick={() => paging.change(setWarehouseIds)(toggle(warehouseIds, warehouse.id))}
                >
                  {warehouse.name}
                </Chip>
              ))}
              <NativeSelect
                aria-label={t('mv.filterCategory')}
                value={categoryId}
                onChange={(event) => paging.change(setCategoryId)(event.target.value)}
                className="h-10 w-auto text-sm"
              >
                <option value="">{t('mv.filterCategoryAll')}</option>
                {(categories ?? []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          ) : null}

          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('mv.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('mv.colCode')}</TableHead>
                  <TableHead>{t('mv.colDate')}</TableHead>
                  <TableHead>{t('mv.colType')}</TableHead>
                  <TableHead>{t('mv.colItem')}</TableHead>
                  <TableHead>{t('mv.colWarehouse')}</TableHead>
                  <TableHead className="text-end">{t('mv.colQty')}</TableHead>
                  <TableHead className="text-end">{t('mv.colBalance')}</TableHead>
                  <TableHead>{t('mv.colReference')}</TableHead>
                  <TableHead>{t('mv.colPerformedBy')}</TableHead>
                  <TableHead>{t('mv.colReason')}</TableHead>
                  {/* The column stays whatever the role holds: BR-PERM-18 —
                      a person who cannot see the door cannot ask for a key. */}
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const isTransfer = row.kind === 'transfer_in' || row.kind === 'transfer_out';
                  return (
                    <TableRow
                      key={row.id}
                      className={cn(
                        'odd:bg-muted/30',
                        // BR-INV-06-09 — a hand-written movement is amber.
                        row.isManual && 'bg-amber-50 dark:bg-amber-950/30',
                        isTransfer &&
                          'border-s-2 border-s-blue-500 bg-blue-50/60 dark:bg-blue-950/20',
                      )}
                    >
                      <TableCell>
                        <Ltr value={row.code} className="font-mono text-sm" />
                      </TableCell>
                      <TableCell>
                        <span className="grid">
                          <Ltr value={row.occurredAt.slice(0, 10)} className="font-mono text-sm" />
                          <Ltr
                            value={row.occurredAt.slice(11, 16)}
                            className="font-mono text-xs text-muted-foreground"
                          />
                        </span>
                      </TableCell>
                      <TableCell>
                        <MovementKindPill kind={row.kind} />
                      </TableCell>
                      <TableCell>
                        <span className="grid">
                          <span className="text-sm font-medium">{row.itemName}</span>
                          <Ltr
                            value={row.itemCode}
                            className="font-mono text-xs text-muted-foreground"
                          />
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.warehouseName}
                        {row.binCode ? (
                          <span className="text-muted-foreground">
                            {' · '}
                            <Ltr value={row.binCode} className="font-mono" />
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className={cn('text-end font-bold', signedQtyClass(row.qty))}>
                        {/* N-4/N-14: sign, the unit's decimals, the unit. */}
                        <Ltr
                          value={signedQtyWithUnit(row.qty, row.unitPrecision, row.unitSymbol)}
                          className="font-mono"
                        />
                      </TableCell>
                      <TableCell className="text-end">
                        {/* Per item PER WAREHOUSE (BR-INV-06-05) — which is
                            also ERR-INV-06-05 closed. INV-06 §14 recorded
                            "Adjusted a multi-location item | The Balance after
                            column is wrong | This must be fixed under
                            BR-INV-06-05": the prototype ran one running total
                            per ITEM, so a movement in one warehouse changed
                            the figure shown against another. Carrying the
                            balance per (item, warehouse) is that fix. */}
                        {/*
                          N-4's decimals; the SYMBOL is on the Qty cell
                          immediately before this one, on the same row and in
                          the same unit by construction. "Next to them" is
                          satisfied per ROW — the same reading INV-05's Unit
                          column gets — so it is not printed twice.
                        */}
                        <Ltr
                          value={formatQty(row.balanceAfter, row.unitPrecision)}
                          className="font-mono"
                        />
                      </TableCell>
                      <TableCell>
                        {/*
                          Plain text: the ledger points at documents, it does
                          not open them.
                          BR-INV-06-14 offers two acceptable builds — "must open
                          the source document, OR must not be styled as a link"
                          — and this is the second, taken deliberately. The
                          reference is one string covering fourteen reference
                          kinds, several of which (a POS sale, a consumption
                          voucher, a movement with reference kind `manual`) have
                          no screen to open at all, so the first branch would be
                          a link that works for some rows and dead-ends on
                          others: the prototype's defect with better coverage.
                          ERR-INV-06-03 — "clicked a reference hoping to open
                          the document | NOTHING HAPPENS" — is closed by the
                          affordance being absent rather than by the click being
                          answered. There is nothing here that invites the
                          click, which is why the disappointment cannot happen.
                          A future change that wraps this in an anchor or adds
                          link styling re-opens that row unless it also wires
                          every kind; `inv-remainder-rules.test.tsx` asserts the
                          ABSENCE, so that change fails a test instead of
                          looking like an improvement.
                        */}
                        <Ltr value={row.referenceNo} className="font-mono text-sm" />
                      </TableCell>
                      <TableCell className="text-sm">{row.performedByName}</TableCell>
                      <TableCell className="max-w-[220px] text-sm text-muted-foreground">
                        {row.reason ?? '—'}
                      </TableCell>
                      <TableCell>
                        {/* A movement already corrected has no second
                            correction for anybody — a LEDGER rule, not a
                            permission one (BR-INV-06-02). */}
                        {corrected.has(row.id) ? null : (
                          <Gated permission="inventory.approve" reasonKey="perm.cannot.correctStock">
                            <button
                              type="button"
                              aria-label={t('mv.correctRow', { code: row.code })}
                              title={t('mv.correct')}
                              onClick={() => setCorrecting(row)}
                              className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
                            >
                              <RotateCcw className="size-4" aria-hidden />
                            </button>
                          </Gated>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/*
            NFR-42 — the ledger's pager. The total beside it is the SAME number
            the "total movements" card shows: the server computes it once over
            the filtered set, so the card and the range cannot disagree.
          */}
          {!isPending && !isError && rows.length > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t('mv.title')}
            />
          ) : null}
        </CardContent>
      </Card>

      {manualOpen ? <ManualAdjustmentModal onClose={() => setManualOpen(false)} /> : null}
      {correcting ? (
        <CorrectModal row={correcting} onClose={() => setCorrecting(null)} />
      ) : null}
    </div>
  );
}
